/*** Locales that may be mapped:
 * en_US - English for US Region
 * en_GB - English for UK Region
 * es_MX - Spanish for Latin American Region
 * fr_CA - French for US (incl. Canada) Region
 * fr_FR - French for France Region
 * de_DE - German for Germany Region
 */

import { LocaleCode } from './locale';
import { getDefaultRegion, RegionCode } from './region';

const localesByRegion: Record<RegionCode, LocaleCode[]> = {
  US: ['en_US', 'es_US', 'fr_CA'],
  UK: ['en_GB'],
  MX: ['es_MX'],
  FR: ['fr_FR'],
  DE: ['de_DE'],
};

export function getSupportedLocales(region: RegionCode): LocaleCode[] {
  return localesByRegion[region] ?? ['en_US']; // default to US English
}

export function getSupportedLanguages(region: RegionCode): string[] {
  return Array.from(new Set(getSupportedLocales(region).map(locale => getLanguage(locale))));
}

export function getDefaultLocale(region: RegionCode): LocaleCode {
  const locales = getSupportedLocales(region);

  return locales[0];
}

export function getRegionForLocale(locale: LocaleCode): RegionCode {
  return (
    (Object.keys(localesByRegion).find(region =>
      localesByRegion[region as RegionCode].includes(locale)
    ) as RegionCode) ?? getDefaultRegion()
  );
}

export function getLanguage(lang: LocaleCode): string;
export function getLanguage(lang: string | null | undefined): string | undefined;
export function getLanguage(lang: string | null | undefined): string | undefined {
  return lang?.split(',')[0].split(/[-_]/)[0] ?? undefined;
}

export function resolveLocale(region: RegionCode, country: string, language: string): LocaleCode {
  const locales = getSupportedLocales(region);

  const exactMatch: LocaleCode = `${language}_${country}`;
  if (locales.includes(exactMatch)) {
    return exactMatch;
  }

  const languageMatch = locales.find(locale => locale.startsWith(`${language}_`));
  if (languageMatch) {
    return languageMatch;
  }

  return getDefaultLocale(region);
}
